import * as React from 'react';
import type { IOption, SelectOpenInteraction, SelectOptionValue } from '../types';
import {
	NEXT_OPTION_OFFSET,
	NO_HIGHLIGHTED_OPTION_INDEX,
	PREVIOUS_OPTION_OFFSET,
} from './selectState.utils';

interface IUseSelectKeyboardProps<TValue extends SelectOptionValue, TMetadata> {
	closeSelect: () => void;
	disabled: boolean;
	enabledOptionIndexes: ReadonlyArray<number>;
	highlightedOption?: IOption<TValue, TMetadata>;
	isOpen: boolean;
	moveHighlight: (offset: number) => void;
	openSelect: (interaction?: SelectOpenInteraction) => void;
	selectOption: (option: IOption<TValue, TMetadata>) => void;
	setHighlightedIndex: (index: number) => void;
	showHighlight: () => void;
}

/**
 * Creates keyboard interactions shared by select triggers and option lists.
 */
export const useSelectKeyboard = <TValue extends SelectOptionValue, TMetadata>({
	closeSelect,
	disabled,
	enabledOptionIndexes,
	highlightedOption,
	isOpen,
	moveHighlight,
	openSelect,
	selectOption,
	setHighlightedIndex,
	showHighlight,
}: IUseSelectKeyboardProps<TValue, TMetadata>): React.KeyboardEventHandler<HTMLElement> =>
	React.useCallback(
		event => {
			if (event.nativeEvent.isComposing) {
				return;
			}

			if (disabled) {
				return;
			}

			if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
				event.preventDefault();
				if (!isOpen) {
					openSelect();
					showHighlight();
					if (event.key === 'ArrowUp') {
						moveHighlight(PREVIOUS_OPTION_OFFSET);
					}
					return;
				}
				showHighlight();
				moveHighlight(event.key === 'ArrowDown' ? NEXT_OPTION_OFFSET : PREVIOUS_OPTION_OFFSET);
				return;
			}

			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				if (!isOpen) {
					openSelect();
					showHighlight();
					return;
				}
				if (highlightedOption) {
					selectOption(highlightedOption);
				}
				return;
			}

			if (isOpen && (event.key === 'Home' || event.key === 'End')) {
				event.preventDefault();
				const boundaryIndex =
					event.key === 'Home'
						? enabledOptionIndexes[0]
						: enabledOptionIndexes[enabledOptionIndexes.length - 1];
				showHighlight();
				setHighlightedIndex(boundaryIndex ?? NO_HIGHLIGHTED_OPTION_INDEX);
				return;
			}

			if (event.key === 'Escape') {
				if (!isOpen) {
					return;
				}
				event.preventDefault();
				event.stopPropagation();
				closeSelect();
				return;
			}

			if (event.key === 'Tab' && isOpen) {
				closeSelect();
			}
		},
		[
			closeSelect,
			disabled,
			enabledOptionIndexes,
			highlightedOption,
			isOpen,
			moveHighlight,
			openSelect,
			selectOption,
			setHighlightedIndex,
			showHighlight,
		]
	);
